/**
 * 이상한 방 - 작업대(초안)의 순수 조각: 게이트 · 남은 예산 · 커밋 여부 · 초안 합치기
 *
 * 근거 문서: `이상한방-상태모델-v0.1.md` 5.1절(한 물건 한 번) · 11.3절(서버가 쥐는 것)
 *            로드맵 M11 규칙 2·3·8(전원이 동시에 자기 구획을 고친다)
 *
 * ## 왜 `matchChange.ts`에서 갈라져 나왔는가
 *
 * 마감(`matchPhases.finishChange`)이 초안을 합치고 구획마다 채운다. 그 코드가 `matchChange.ts`에 있으면
 * `matchPhases → matchChange → matchPhases`로 import가 고리를 이룬다(`matchChange`는 커밋에서 `finishChange`를 부른다).
 * 그래서 **양쪽이 함께 읽는 순수 조각**만 여기 둔다 - 이벤트 처리는 `matchChange.ts`, 페이즈 전환은 `matchPhases.ts`,
 * 둘 다 이 파일을 본다. 규칙의 사본을 만들지 않으면서 고리를 끊는 유일한 자리다.
 *
 * 여기 있는 것은 전부 **읽기**와 **작업대 하나를 바꾼 사본 만들기**이고, 상태를 스스로 바꾸지 않는다.
 */

import { addObject, removeObject, updateObject } from '../../core/room'
import type { PlayerId, RoomState } from '../../core/types'
import { seatsInOrder } from './matchSelectors'
import type { ChangeDesk, MatchChange, MatchPlayer, MatchState } from './matchTypes'

/* --- 게이트 ---------------------------------------------------------------------- */

/**
 * 이 사람이 지금 자기 작업대를 만질 수 있는가(바꾸기 페이즈 · 작업대 있음 · 아직 커밋 전). 아니면 `null` -
 * 이벤트는 참조 동일로 무시된다. 커밋한 사람이 계속 만질 수 있으면 [다 했다]가 뜻을 잃는다.
 */
export function activeDesk(state: MatchState, id: PlayerId): ChangeDesk | null {
  if (state.phase !== 'change' || state.desks === null) return null
  const desk = state.desks[id]
  if (desk === undefined || desk.committed) return null
  return desk
}

/**
 * 위 게이트의 불리언 얼굴. 투영(`matchView.selfOf`)이 `canShoot`처럼 **그대로 호출**해 `self.canChange`로 내린다 -
 * 클라이언트(도구·무대·대기 화면)가 `phase === 'change'`를 각자 적으면 그것이 게이트의 사본이다.
 */
export function canUseDesk(state: MatchState, id: PlayerId): boolean {
  return activeDesk(state, id) !== null
}

/**
 * 남은 예산. 투영(`matchView.deskFor`)이 `desk.budgetLeft`로 그대로 내리고, 도구는 그 수에 답을 기다리는 요청만 얹는다 -
 * `budget - used`를 클라이언트가 다시 세면 예산 규칙이 두 벌이다(로드맵 모델 2.5절).
 */
export function budgetLeftOf(desk: ChangeDesk): number {
  return desk.budget - desk.changes.length
}

/** 예산이 남았는가. `budgetLeftOf` 하나에서 나온다. */
export function hasBudgetLeft(desk: ChangeDesk): boolean {
  return budgetLeftOf(desk) > 0
}

/**
 * 바꾸기가 끝나도 되는가 - **손으로 고칠 사람이 전부 커밋했는가.**
 *
 * 이웃(CPU)과 끊긴 좌석은 자동으로 끝난 것으로 친다(M11 규칙 7 "이웃은 즉시 완료로 친다"). 이웃도 `cpuDecide`가
 * 실제로 `commitChanges`를 보내지만(9.3절 - 같은 큐), 여기서 한 번 더 봐 주는 이유는 호스트의 스케줄이 늦어도
 * 60초가 헛되이 흐르지 않게 하기 위해서다. 두 길의 답은 같다.
 */
export function everyoneCommitted(state: MatchState): boolean {
  if (state.desks === null) return false
  return seatsInOrder(state).every((player) => deskDone(state, player))
}

/**
 * 이 좌석은 더 기다릴 필요가 없는가. 마감 판정(`everyoneCommitted`)과 투영(`matchView.publicPlayer.changeDone`)이
 * **같은 함수**를 부른다 - 갈라지면 대기 화면이 "이웃을 기다리는 중"을 영원히 그리는 동안 리듀서는 이미 넘어가 있다.
 */
export function deskDone(state: MatchState, player: MatchPlayer): boolean {
  if (player.kind !== 'human' || !player.connected) return true
  return state.desks?.[player.id]?.committed === true
}

/* --- 초안 합치기 ------------------------------------------------------------------ */

/**
 * 초안 여럿 → 변경본 하나(머리말 "합치기는 원본 위에 각자의 변화만 얹는다").
 *
 * 각 사람의 `changes`가 가리키는 대상만 그 사람의 초안 모습으로 옮긴다. 초안 전체를 덮어쓰지 않는 이유는 초안이
 * **원본의 사본**이라서다 - 뒤에 오는 초안이 앞 사람의 변화를 되돌린다. 세 갈래뿐이고 전부 `core/room`의 함수다:
 * 초안에 없으면 지운 것, 변경본에 있으면 바꾼 것, 없으면 새로 놓은 것.
 */
export function mergeDrafts(
  original: RoomState,
  desks: Readonly<Record<PlayerId, ChangeDesk>>,
  order: readonly PlayerId[],
): RoomState {
  let merged = original
  for (const id of order) {
    const desk = desks[id]
    if (desk === undefined) continue
    for (const change of desk.changes) {
      const after = desk.draft.objects.get(change.targetId)
      if (after === undefined) {
        merged = merged.objects.has(change.targetId) ? removeObject(merged, change.targetId) : merged
      } else if (merged.objects.has(change.targetId)) {
        merged = updateObject(merged, change.targetId, { cell: after.cell, rot: after.rot, colorId: after.colorId })
      } else {
        merged = addObject(merged, after)
      }
    }
  }
  return merged
}

/** 작업대 전부의 변화를 좌석 순서로 편다. 순서가 곧 리빌·번호의 순서라 좌석 순서 하나로 고정한다. */
export function deskChanges(
  desks: Readonly<Record<PlayerId, ChangeDesk>>,
  order: readonly PlayerId[],
): readonly MatchChange[] {
  const changes: MatchChange[] = []
  for (const id of order) {
    const desk = desks[id]
    if (desk === undefined) continue
    changes.push(...desk.changes)
  }
  return changes
}

/** 작업대 하나를 갈아 끼운 상태. 나머지 작업대는 같은 참조를 유지한다. */
export function withDesk(state: MatchState, id: PlayerId, desk: ChangeDesk): MatchState {
  return { ...state, desks: { ...(state.desks ?? {}), [id]: desk } }
}

/**
 * 좌석 전부의 빈 작업대. 초안은 원본 그대로에서 시작한다 - 아직 아무도 손대지 않았다.
 * **좌석 전부**에 세우는 이유는 이웃·끊긴 좌석의 구획도 마감에서 채워져야 하기 때문이다(M11 규칙 8) -
 * 작업대가 없는 구획은 채움의 대상 목록에서 빠지고, 그 구획만 원본으로 남는 라운드가 된다.
 */
export function freshDesks(state: MatchState, budget: number): Readonly<Record<PlayerId, ChangeDesk>> {
  const desks: Record<PlayerId, ChangeDesk> = {}
  for (const player of seatsInOrder(state)) {
    desks[player.id] = { budget, touched: [], draft: state.original, changes: [], committed: false }
  }
  return desks
}
